import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { TrendingUp } from "lucide-react";
import { api, fmt } from "@/lib/api";

export const TrendChart = ({ refreshKey }) => {
  const [data, setData] = useState([]);

  useEffect(() => {
    api.trends().then((r) => setData(r.trends || [])).catch(() => setData([]));
  }, [refreshKey]);

  const best = data.reduce((a, b) => (b.net_profit > (a?.net_profit ?? -Infinity) ? b : a), null);
  const worst = data.reduce((a, b) => (b.net_profit < (a?.net_profit ?? Infinity) ? b : a), null);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
      data-testid="trend-view"
    >
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp className="h-5 w-5 text-gray-900" />
          <h3 className="font-heading text-xl font-bold text-gray-900">Month-over-Month Profitability</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Income, expenses and net profit across every month you've imported.
        </p>

        {data.length === 0 ? (
          <div className="text-sm text-gray-400 py-16 text-center">
            No data yet. Import statements across a few months to see the trend.
          </div>
        ) : (
          <div className="h-80" style={{ minHeight: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "#6B7280" }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => fmt(v)} cursor={{ fill: "#F9FAFB" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="income" name="Income" fill="#059669" radius={[4, 4, 0, 0]} maxBarSize={28} />
                <Bar dataKey="expense" name="Expenses" fill="#DC2626" radius={[4, 4, 0, 0]} maxBarSize={28} />
                <Line
                  type="monotone"
                  dataKey="net_profit"
                  name="Net Profit"
                  stroke="#0A0A0A"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: "#0A0A0A" }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {data.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm" data-testid="trend-best-month">
            <span className="text-xs tracking-[0.2em] uppercase font-semibold text-gray-500">Best month</span>
            <div className="font-mono text-2xl font-semibold text-emerald-600 mt-3">{fmt(best?.net_profit)}</div>
            <div className="text-sm text-gray-500 mt-1">{best?.label}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm" data-testid="trend-worst-month">
            <span className="text-xs tracking-[0.2em] uppercase font-semibold text-gray-500">Weakest month</span>
            <div className={`font-mono text-2xl font-semibold mt-3 ${worst?.net_profit < 0 ? "text-rose-600" : "text-gray-900"}`}>
              {fmt(worst?.net_profit)}
            </div>
            <div className="text-sm text-gray-500 mt-1">{worst?.label}</div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm" data-testid="trend-months-tracked">
            <span className="text-xs tracking-[0.2em] uppercase font-semibold text-gray-500">Months tracked</span>
            <div className="font-mono text-2xl font-semibold text-gray-900 mt-3">{data.length}</div>
            <div className="text-sm text-gray-500 mt-1">
              {data[0]?.label} – {data[data.length - 1]?.label}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};
